import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { SlideToggle } from '@ygtechnologies/bygr-frontend-component-library';
import { searchProjectDocuments, ProjectDocumentSearchOptions } from '@/services/documents';
import { ProjectPermissionToggle } from '@/components/permissions';
import { faUpload, faFileArchive } from '@fortawesome/pro-light-svg-icons';
import { DocumentStatus } from '@/types';
import { useEffectSkipFirst } from '@/utils/hooks';
import DocumentUploadDialog from './DocumentUploadDialog';
import DocumentUploadWizardDialog from './DocumentUploadWizardDialog';
import DocumentZipDownloadDialog from './DocumentZipCreateDialog';
import DocumentsFilter from './DocumentsFilter';
import { useHistory, useLocation } from 'react-router';
import { AlertBanner, Button, ButtonBar, Tabs } from '@ygtechnologies/rubus-ui';

const ALL_STATUS = 'all';
import ProjectDocumentFilterableList from './ProjectDocumentFilterableList';

interface ProjectDocumentsProps {
    projectId: number;
}

function getDocumentsFilter(projectId: number, status: DocumentStatus | null): ProjectDocumentSearchOptions {
    return {
        status: status ? [status] : undefined,
        includeArchived: false,
        categories: [],
        projectId: projectId,
        IncludeUnpublishedVersionsCount: true,
    };
}

const ProjectDocuments: React.FC<ProjectDocumentsProps> = ({ projectId }) => {
    const history = useHistory();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const statusFromUrl = urlParams.get('status') as DocumentStatus | null;

    const [selectedStatus, setSelectedStatus] = useState<DocumentStatus | null>(statusFromUrl);
    const [pendingCount, setPendingCount] = useState<number>(0);

    const defaultFilter = useMemo(() => {
        return getDocumentsFilter(projectId, selectedStatus);
    }, [projectId, selectedStatus]);

    const [filters, setFilters] = useState(defaultFilter);
    const [documentZipDialogIsOpen, setDocumentZipDialogIsOpen] = useState(false);

    useEffectSkipFirst(() => {
        setFilters(defaultFilter);
    }, [defaultFilter]);

    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [showUploadWizardDialog, setShowUploadWizardDialog] = useState(false);

    const [uploadedDocument, setUploadedDocument] = useState<any>(null);

    const handleStatusChange = (status: DocumentStatus | null) => {
        setSelectedStatus(status);
        const params = new URLSearchParams(location.search);
        if (status) {
            params.set('status', status.toLowerCase());
        } else {
            params.delete('status');
        }
        history.push({ search: params.toString() });
    };

    // Fetch pending document count
    useEffect(() => {
        const fetchPendingCount = async () => {
            const pendingDocs = await searchProjectDocuments({
                projectId,
                includeArchived: filters.includeArchived,
                categories: filters.categories || [],
                IncludeUnpublishedVersionsCount: true,
                status: [DocumentStatus.Pending],
            });

            setPendingCount(pendingDocs.length);
        };

        fetchPendingCount();
    }, [projectId, filters.includeArchived, filters.categories]);

    const handleDocumentsUploaded = () => {
        // Force reload by updating filters
        setFilters({ ...filters });
    };

    return (
        <React.Fragment>
            <ButtonBar sticky margins="none" stickyClassName="stickyBleed">
                <Tabs
                    activeId={selectedStatus ?? ALL_STATUS}
                    onChange={(id) => handleStatusChange(id === ALL_STATUS ? null : (id as DocumentStatus))}
                    variant="pill"
                    size="sm">
                    <Tabs.Tab id={ALL_STATUS} label={<Trans>All</Trans>} />
                    <Tabs.Tab id={DocumentStatus.Pending} label={<Trans>Pending</Trans>} badge={pendingCount} />
                    <Tabs.Tab id={DocumentStatus.Approved} label={<Trans>Published</Trans>} />
                    <Tabs.Tab id={DocumentStatus.Rejected} label={<Trans>Rejected</Trans>} />
                </Tabs>

                <ButtonBar.Right>
                    <SlideToggle
                        onChange={(e) => setFilters({ ...filters, includeArchived: e.target.checked })}
                        checked={filters.includeArchived}>
                        <Trans>Show archived documents</Trans>
                    </SlideToggle>
                    <Button onClick={() => setDocumentZipDialogIsOpen(true)} icon={faFileArchive} variant="tertiary">
                        <Trans>Create a zip file</Trans>
                    </Button>
                    <ProjectPermissionToggle condition="canUploadDocuments">
                        <Button variant="tertiary" onClick={(e) => setShowUploadWizardDialog(true)} icon={faUpload}>
                            <Trans>Upload project document</Trans>
                        </Button>
                    </ProjectPermissionToggle>
                    <DocumentsFilter
                        defaultFilter={defaultFilter}
                        filter={filters}
                        onChange={(v: ProjectDocumentSearchOptions) => setFilters(v)}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            <AlertBanner type="info">
                <Trans>
                    Here you can upload a document that applies to multiple units. Remember to link the document to the
                    relevant units after uploading.
                </Trans>
            </AlertBanner>

            <ProjectDocumentFilterableList filters={filters} />

            <DocumentZipDownloadDialog
                isProjectDocuments
                filters={filters}
                toggle={() => setDocumentZipDialogIsOpen(false)}
                isOpen={documentZipDialogIsOpen}
            />

            <DocumentUploadDialog
                document={uploadedDocument || null}
                isOpen={showUploadDialog}
                projectId={projectId}
                onSave={handleDocumentsUploaded}
                onClose={() => {
                    setShowUploadDialog(false);
                    setUploadedDocument(null);
                }}
            />

            <DocumentUploadWizardDialog
                document={uploadedDocument || null}
                isOpen={showUploadWizardDialog}
                projectId={projectId}
                onSave={handleDocumentsUploaded}
                onClose={() => {
                    setShowUploadWizardDialog(false);
                    setUploadedDocument(null);
                }}
            />
        </React.Fragment>
    );
};

export default ProjectDocuments;
